import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { DEFAULT_DPI, setDPI } from '@meitu/saas-editor-core/es/utils/unit-kit';
import { CanvasType, IDynamicInfoProps, RenderMode } from '@meitu/saas-editor-core/es/version-types';
import { editor } from '@/instance';
import { TemplateResponseData } from '@/types';

interface IAiPPTInfoProps {
    aiStyleSelect: { name: string; value: number }[];
    aiFuncSelect: { name: string; value: number; icon: string }[];
    ai_tone_select: { name: string; value: number }[];
}

interface IInitTemplateInfo {
    /** 是否由作图记录初始化 */
    initFromRecord: boolean;
    /** PPT 主题 */
    subject: string;
    preview: string;
    version: string;
    id: string;
    templateName: string;
    canvasType: CanvasType;
    aiPPTInfo?: IAiPPTInfoProps;
    originMaterialId?: string;
    aiPicMapByLayerId?: { [key: string]: any[] };
    resolution: number;
    extraInfo?: { [key: string]: any };
    dynamicBgInfo?: { dynamicInfo: IDynamicInfoProps; previewUrl: string };
    isArray?: boolean;
    psdFileUrl?: string;
    qrCode?: string;
    templateId?: string;
    mcpSourceId?: string;
    useWatermark: boolean;
    tagInfoId?: string;
}

export interface ITemplateModel extends IInitTemplateInfo {
    template: TemplateResponseData | undefined;
    destroy: () => void;
    setExtraInfo: (payload: { [key: string]: any }) => void;
    setTemplate: (payload: TemplateResponseData | undefined) => void;
    setTemplateName: (payload: string) => void;
    setTemplateId: (payload: string) => void;
    setVersion: (payload: string) => void;
    setPreview: (payload: string) => void;
    setCanvasType: (payload: CanvasType) => void;
    setResolution: (payload: number) => void;
    initTemplateInfo: (payload: IInitTemplateInfo) => void;
    setAiImageRemain: (payload: number) => void;
    setAiImageInfo: (payload: IAiPPTInfoProps) => void;
    setOriginMaterialId: (payload: string) => void;
    setAiPicMapByLayerId: (payload: { [key: string]: any[] }) => void;
    isReplaceTemplate: boolean;
    setIsReplaceTemplate: (payload: boolean) => void;
    canShowReplaceAiPPTCollapse: boolean;
    setCanReplaceAiPPTCollapse: (payload: boolean) => void;
    hasReplaceAiPPTTemplate: boolean;
    setHasReplaceAiPPTTemplate: (payload: boolean) => void;
    setDynamicBgInfo: (payload: { dynamicInfo: IDynamicInfoProps; previewUrl: string } | undefined) => void;
    setPsdFileUrl: (payload: string) => void;
    setQrCode: (payload: string) => void;
    setServerTemplateId: (payload: string) => void;
    setMCPSourceId: (payload: string) => void;
    setUseWatermark: (payload: boolean) => void;
    setTagInfoId: (payload: string) => void;
}

/** 同步编辑器画布模式 */
function setEditorInstanceCanvasType(canvasType: CanvasType) {
    editor.services.edgelessCanvas.setRenderMode(
        canvasType === CanvasType.VARIETY ? RenderMode.VARIETY : RenderMode.SINGLE,
    );
    editor.services.editor.setCanvasType(canvasType);
}

const useTemplateModel = create<ITemplateModel>()(
    immer((set) => ({
        destroy: () =>
            set((state) => {
                state.originMaterialId = '';
                state.aiPicMapByLayerId = {};
                state.initFromRecord = false;
                state.subject = '';
                state.template = undefined;
                state.templateName = '';
                state.preview = '';
                state.version = '';
                state.id = '';
                state.canvasType = CanvasType.NORMAL;
                state.resolution = DEFAULT_DPI;
            }),
        hasReplaceAiPPTTemplate: false,
        setHasReplaceAiPPTTemplate: (payload) =>
            set((state) => {
                state.hasReplaceAiPPTTemplate = payload;
            }),
        canShowReplaceAiPPTCollapse: false,
        setCanReplaceAiPPTCollapse: (payload) =>
            set((state) => {
                state.canShowReplaceAiPPTCollapse = payload;
            }),
        dynamicBgInfo: undefined,
        setDynamicBgInfo: (payload) =>
            set((state) => {
                state.dynamicBgInfo = payload;
            }),
        extraInfo: {},
        setExtraInfo: (payload) =>
            set((state) => {
                state.extraInfo = { ...state.extraInfo, ...payload };
                if (state.extraInfo?.masterPage) {
                    editor.services.customData.setCustomData('masterPage', state.extraInfo.masterPage.layers);
                }
            }),
        aiPPTInfo: { aiStyleSelect: [], ai_tone_select: [], aiFuncSelect: [] },
        originMaterialId: '',
        aiPicMapByLayerId: {},
        initFromRecord: false,
        subject: '',
        template: undefined,
        templateName: '',
        preview: '',
        version: '',
        id: '',
        resolution: DEFAULT_DPI,
        canvasType: CanvasType.NORMAL,
        isReplaceTemplate: false,
        setIsReplaceTemplate: (payload) =>
            set((state) => {
                state.isReplaceTemplate = payload;
            }),
        initTemplateInfo: (payload) =>
            set((state) => {
                state.id = payload.id;
                state.preview = payload.preview;
                state.version = payload.version;
                state.templateName = payload.templateName;
                state.canvasType = payload.canvasType;
                state.initFromRecord = payload.initFromRecord;
                state.subject = payload.subject;
                state.originMaterialId = payload.originMaterialId;
                state.resolution = payload.resolution;
                setEditorInstanceCanvasType(payload.canvasType);
            }),
        setCanvasType: (payload) =>
            set((state) => {
                state.canvasType = payload;
                setTimeout(() => setEditorInstanceCanvasType(payload), 0);
            }),
        setTemplateId: (payload) =>
            set((state) => {
                state.id = payload;
            }),
        setVersion: (payload) =>
            set((state) => {
                state.version = payload;
            }),
        setPreview: (payload) =>
            set((state) => {
                state.preview = payload;
            }),
        setTemplate: (payload) =>
            set((state) => {
                state.template = payload;
            }),
        setTemplateName: (payload) =>
            set((state) => {
                state.templateName = payload;
            }),
        setAiImageRemain: () =>
            set((state) => {
                if (!state.aiPPTInfo) return;
                state.aiPPTInfo = { ...state.aiPPTInfo };
            }),
        setAiImageInfo: (payload) =>
            set((state) => {
                state.aiPPTInfo = payload;
            }),
        setOriginMaterialId: (payload) =>
            set((state) => {
                state.originMaterialId = payload;
            }),
        setAiPicMapByLayerId: (payload) =>
            set((state) => {
                state.aiPicMapByLayerId = Object.assign({}, state.aiPicMapByLayerId, payload);
            }),
        setResolution: (payload) =>
            set((state) => {
                setDPI(payload);
                state.resolution = payload;
            }),
        psdFileUrl: '',
        setPsdFileUrl: (payload) =>
            set((state) => {
                state.psdFileUrl = payload;
            }),
        qrCode: '',
        setQrCode: (payload) =>
            set((state) => {
                state.qrCode = payload;
            }),
        templateId: undefined,
        setServerTemplateId: (payload) =>
            set((state) => {
                state.templateId = payload;
            }),
        mcpSourceId: undefined,
        setMCPSourceId: (payload) =>
            set((state) => {
                state.mcpSourceId = payload;
            }),
        useWatermark: true,
        setUseWatermark: (payload) =>
            set((state) => {
                state.useWatermark = payload;
            }),
        tagInfoId: undefined,
        setTagInfoId: (payload) =>
            set((state) => {
                state.tagInfoId = payload;
            }),
    })),
);

export { useTemplateModel };

/**
 * 获取模板状态
 *
 * @returns 当前模板 Store 状态
 */
export const getTemplateModel = () => useTemplateModel.getState();
